import React, { useState } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import moment from 'moment';
import {
  Button,
  Grid2 as Grid,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Fab,
  DialogActions,
  Paper,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AddOutlined, Delete } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers';
import * as Yup from 'yup';
import FieldArrayErrorMessage from '../../Form/FieldArrayErrorMessage';
import { DATE_FORMAT_SLASHED } from '../../../helpers/dates';
import { GridContainer } from '../../Form/shared';
import withDeletePopup from '../../withDeletePopup';

const useStyles = makeStyles(theme => ({
  areasSelectFormControl: {
    width: '100%',
  },
  userRow: {
    marginBottom: theme.spacing(2),
  },
  datePicker: {
    width: '100%',
  },
  addButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  actions: {
    width: '100%',
    position: 'sticky',
    bottom: -theme.spacing(1.25),
    right: 0,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  finalActions: {
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  cancelButton: {
    marginLeft: theme.spacing(2),
  },
  removeButtonContainer: {
    marginTop: theme.spacing(1),
  },
  userPaper: {
    paddingLeft: theme.spacing(2),
    paddingRigh: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  errorInput: {},
}));

const validationSchema = Yup.object().shape({
  allocatedStaff: Yup.array().of(
    Yup.object().shape({
      user: Yup.object().nullable().required('User is required'),
    })
  ),
});

const StaffAllocationForm = ({
  users,
  event,
  onSubmit,
  onClose,
  onDeleteConfirmationDialogOpen,
}) => {
  const classes = useStyles();

  const [userOptions, setUserOptions] = useState([...users]);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        allocatedStaff: event.allocatedStaff.length
          ? event.allocatedStaff
          : [
              {
                user: null,
                areas: [],
                from: event.start,
                to: event.start,
              },
            ],
      }}
      onSubmit={onSubmit}
    >
      <Form>
        <FieldArray name="allocatedStaff">
          {fieldArrayProps => {
            const { push, form, remove } = fieldArrayProps;
            const { values, setFieldValue } = form;
            const { allocatedStaff } = values;
            const areas = [
              'Check-in',
              'Gate',
              'Ramp',
              'Transfer desk',
              'Disruption',
              'Transport',
            ];

            return (
              <>
                {allocatedStaff.map((user, index) => {
                  const isAllAreasSelected =
                    allocatedStaff[index].areas.length > 0 &&
                    allocatedStaff[index].areas.length === areas.length;

                  return (
                    <Paper className={classes.userPaper}>
                      <GridContainer
                        key={index}
                        alignItems="center"
                        className={classes.userRow}
                      >
                        <GridContainer xs={10}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                              id={`${user._id}-select`}
                              options={userOptions}
                              getOptionLabel={user =>
                                `${user.firstName} ${user.lastName}`
                              }
                              name={`allocatedStaff[${index}].user`}
                              value={allocatedStaff[index].user}
                              onChange={(_, value) => {
                                const newUserOptions = userOptions.filter(
                                  option => option._id !== value._id
                                );
                                setUserOptions(newUserOptions);
                                setFieldValue(
                                  `allocatedStaff[${index}].user`,
                                  value
                                );
                              }}
                              renderInput={params => (
                                <TextField
                                  variant="standard"
                                  error={allocatedStaff[index].user === null}
                                  {...params}
                                  label="User"
                                  required
                                />
                              )}
                            />
                            <FieldArrayErrorMessage
                              name={`allocatedStaff[${index}].user`}
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 12, md: 6 }}
                            style={{ position: 'relative' }}
                          >
                            <FormControl
                              variant="standard"
                              className={classes.areasSelectFormControl}
                            >
                              <InputLabel
                                error={allocatedStaff[index].areas.length === 0}
                                id="areas-select-label"
                              >
                                Areas
                              </InputLabel>
                              <Select
                                variant="standard"
                                multiple
                                name={`allocatedStaff[${index}].areas`}
                                labelId="areas-select-label"
                                id="areas-select"
                                value={allocatedStaff[index].areas}
                                error={allocatedStaff[index].areas.length === 0}
                                renderValue={selected => selected.join('/')}
                                onChange={e => {
                                  let value = e.target.value;

                                  if (e.target.value.includes('all')) {
                                    if (isAllAreasSelected) {
                                      value = [];
                                    } else {
                                      value = areas;
                                    }
                                  }

                                  setFieldValue(
                                    `allocatedStaff[${index}].areas`,
                                    value
                                  );
                                }}
                              >
                                <MenuItem key="all" value="all">
                                  <ListItemText>All</ListItemText>
                                  <ListItemIcon>
                                    <Checkbox
                                      color="primary"
                                      checked={isAllAreasSelected}
                                    />
                                  </ListItemIcon>
                                </MenuItem>
                                {areas.map(area => (
                                  <MenuItem key={area} value={area}>
                                    <ListItemText>{area}</ListItemText>
                                    <ListItemIcon>
                                      <Checkbox
                                        color="primary"
                                        checked={
                                          allocatedStaff[index].areas.indexOf(
                                            area
                                          ) > -1
                                        }
                                      />
                                    </ListItemIcon>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <DatePicker
                              className={classes.datePicker}
                              value={
                                allocatedStaff[index].from != null
                                  ? moment(allocatedStaff[index].from)
                                  : null
                              }
                              onChange={date =>
                                setFieldValue(
                                  `allocatedStaff[${index}].from`,
                                  date
                                )
                              }
                              error={allocatedStaff[index].from == null}
                              format={DATE_FORMAT_SLASHED}
                              label="From"
                              slotProps={{
                                field: {
                                  variant: 'standard',
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <DatePicker
                              className={classes.datePicker}
                              value={
                                allocatedStaff[index].to != null
                                  ? moment(allocatedStaff[index].to)
                                  : null
                              }
                              error={allocatedStaff[index].to == null}
                              onChange={date =>
                                setFieldValue(
                                  `allocatedStaff[${index}].to`,
                                  date
                                )
                              }
                              format={DATE_FORMAT_SLASHED}
                              label="To"
                              slotProps={{
                                field: {
                                  variant: 'standard',
                                },
                              }}
                            />
                          </Grid>
                        </GridContainer>
                        <Grid
                          className={classes.removeButtonContainer}
                          size={{ xs: 2 }}
                          container
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Fab
                            onClick={() => {
                              onDeleteConfirmationDialogOpen(() =>
                                remove(index)
                              );
                            }}
                            size="small"
                            color="primary"
                          >
                            <Delete />
                          </Fab>
                        </Grid>
                      </GridContainer>
                    </Paper>
                  );
                })}

                <DialogActions className={classes.actions}>
                  <div className={classes.addButtonWrapper}>
                    {userOptions.length > 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddOutlined />}
                        onClick={() =>
                          push({
                            user: null,
                            areas: [],
                            from: event.start,
                            to: event.start,
                          })
                        }
                      >
                        Add user
                      </Button>
                    )}
                  </div>
                  <div className={classes.finalActions}>
                    <Button type="submit" variant="contained" color="primary">
                      Save
                    </Button>
                    <Button
                      type
                      onClick={onClose}
                      variant="text"
                      color="primary"
                      className={classes.cancelButton}
                    >
                      Cancel
                    </Button>
                  </div>
                </DialogActions>
              </>
            );
          }}
        </FieldArray>
      </Form>
    </Formik>
  );
};

export default withDeletePopup(StaffAllocationForm);
